import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TenantService } from 'src/app/shared/services/tenant/tenant.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { SocketsService } from 'src/app/views/sockets.service';
import { LiveTranscriptionPayload, TranscriptionModels } from '../audio-recorder/audio-recorder.dto';
import { RecordingIndicatorComponent } from '../audio-recorder/recording-indicator/recording-indicator.component';

@Component({
  selector: 'app-live-dictation-button',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, TranslateModule, RecordingIndicatorComponent],
  templateUrl: './live-dictation-button.component.html',
  styleUrls: ['./live-dictation-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveDictationButtonComponent implements OnDestroy {
  @Input({ required: true }) chunkId!: string;
  @Input({ required: true }) contentId!: string;

  isRecording = signal(false);
  loading = signal(false);

  private socketService = inject(SocketsService);
  private chunkService = inject(ChunkService);
  private contentStateService = inject(ContentStateService);
  private translate = inject(TranslateService);
  private toast = inject(ToastService);
  private tenantService = inject(TenantService);

  audioStream = signal<MediaStream | null>(null);
  private transcriptionAudioContext: AudioContext | null = null;
  private transcriptionWorkletNode: any = null;
  private transcriptionMicSource: any = null;

  private interimTranscription = '';
  private finalTranscription = '';
  private socketSub: Subscription | null = null;

  async toggleDictation() {
    if (this.isRecording()) {
      this.stopDictation();
    } else {
      await this.startDictation();
    }
  }

  async startDictation() {
    this.loading.set(true);
    this.finalTranscription = '';
    this.interimTranscription = '';

    try {
      await this.socketService.initSocket();
    } catch (err) {
      console.error('Failed to init socket', err);
      this.loading.set(false);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
      return;
    }

    this.chunkService.initializeLiveTranscription({}).subscribe({
      next: async (output) => {
        if (!output) {
          this.loading.set(false);
          this.toast.snackbar.open(this.translate.instant('Toastr.notEnoughCredits'), 'Ok');
          return;
        }

        // We set it as dictation target
        await this.chunkService.setLiveTranscriptionChunkId(this.chunkId);
        this.contentStateService.setLiveTranscriptionState(true, 'dictation');
        this.chunkService.setLiveTranscriptionStartedValue(true);

        this.setupSocketListeners();
        this.socketService.emitEvent('start-stream', {});

        try {
          let audioConstraints: any = { echoCancellation: true };
          const savedMic = localStorage.getItem('selectedMicrophone');

          if (savedMic && savedMic !== 'default') {
            audioConstraints.deviceId = { exact: savedMic };
          }

          let media: MediaStream;
          try {
            media = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
          } catch (deviceError) {
            console.warn('[LiveDictation] Failed to acquire exact microphone, falling back to default/ideal constraints:', deviceError);
            // Fallback to ideal if exact failed (e.g. device disconnected or echoCancellation conflict)
            audioConstraints = { echoCancellation: true };
            if (savedMic && savedMic !== 'default') {
              audioConstraints.deviceId = { ideal: savedMic };
            }
            media = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
          }

          this.audioStream.set(media);

          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          if (AudioContextClass) {
            const audioCtx = new AudioContextClass({ sampleRate: 16000 });
            this.transcriptionAudioContext = audioCtx;

            const micSource = audioCtx.createMediaStreamSource(media);
            this.transcriptionMicSource = micSource;

            const workletCode = `
              class DictationProcessor extends AudioWorkletProcessor {
                process(inputs, outputs, parameters) {
                  const input = inputs[0];
                  if (input && input.length > 0) {
                    const float32Data = input[0];
                    const int16Buffer = new Int16Array(float32Data.length);
                    for (let i = 0; i < float32Data.length; i++) {
                      const s = Math.max(-1, Math.min(1, float32Data[i]));
                      int16Buffer[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
                    }
                    this.port.postMessage(int16Buffer.buffer, [int16Buffer.buffer]);
                  }
                  return true;
                }
              }
              registerProcessor('dictation-processor', DictationProcessor);
            `;
            const blob = new Blob([workletCode], { type: 'application/javascript' });
            const workletUrl = URL.createObjectURL(blob);
            
            await audioCtx.audioWorklet.addModule(workletUrl);
            const workletNode = new AudioWorkletNode(audioCtx, 'dictation-processor');
            this.transcriptionWorkletNode = workletNode;

            micSource.connect(workletNode);
            // Connect to destination to prevent garbage collection in some browsers
            workletNode.connect(audioCtx.destination); 

            workletNode.port.onmessage = (event: MessageEvent) => {
              const liveTranscriptionData: LiveTranscriptionPayload = {
                data: event.data,
                contentID: this.contentId,
                language: this.translate.currentLang,
                settings: { model: TranscriptionModels.CHIRP3 },
                tenantId: this.tenantService.activeTenantId() || localStorage.getItem('active_workspace') || undefined,
              };

              this.socketService.emitEvent('transcribe-data', liveTranscriptionData);
            };

            if (audioCtx.state === 'suspended') {
              audioCtx.resume();
            }
          }

          this.isRecording.set(true);
          this.loading.set(false);
        } catch (err) {
          console.error('Error starting dictation microphone:', err);
          this.loading.set(false);
          this.stopDictation();
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 3000 });
        }
      },
      error: (err) => {
        this.loading.set(false);
        console.error(err);
      },
    });
  }

  private setupSocketListeners() {
    this.socketSub?.unsubscribe();
    this.socketSub = this.socketService.streamEvent$.subscribe((event: any) => {
      const eventName = event.type;
      const data = event.data;

      switch (eventName) {
        case 'transcription-interim-data':
          if (data && data.text && data.contentID === this.contentId) {
            this.interimTranscription = data.text;
            this.emitTranscription();
          }
          break;
        case 'transcription-data':
          if (data && data.text && data.contentID === this.contentId) {
            this.finalTranscription += (this.finalTranscription ? ' ' : '') + data.text;
            this.interimTranscription = '';
            this.emitTranscription();
          }
          break;
        case 'stopped-stream':
        case 'stream-end':
        case 'disconnected':
          this.stopDictation();
          break;
      }
    });
  }

  private emitTranscription() {
    const html = this.getTranscriptionHtml(this.finalTranscription, this.interimTranscription);
    this.chunkService.passLiveTranscriptionData(html, false);
  }

  private getTranscriptionHtml(finalText: string, interimText?: string): string {
    const lines = finalText ? finalText.split('\n') : [];
    if (interimText) {
      if (lines.length > 0) {
        const lastLine = lines[lines.length - 1];
        lines[lines.length - 1] = lastLine ? `${lastLine} <i>${interimText}</i>` : `<i>${interimText}</i>`;
      } else {
        lines.push(`<i>${interimText}</i>`);
      }
    }
    return lines.map((line) => (line ? `${line}` : '')).join('<br>');
  }

  stopDictation() {
    if (!this.isRecording()) return;
    this.isRecording.set(false);
    this.loading.set(false);
    // Pass final data one last time to clear interim formatting before clearing state
    this.chunkService.passLiveTranscriptionData(this.getTranscriptionHtml(this.finalTranscription), true);

    this.chunkService.setLiveTranscriptionStartedValue(false);
    this.contentStateService.setLiveTranscriptionState(false);

    if (this.transcriptionWorkletNode) {
      try {
        this.transcriptionWorkletNode.port.onmessage = null;
        this.transcriptionWorkletNode.disconnect();
      } catch (e) {}
      this.transcriptionWorkletNode = null;
    }
    if (this.transcriptionMicSource) {
      try {
        this.transcriptionMicSource.disconnect();
      } catch (e) {}
      this.transcriptionMicSource = null;
    }
    if (this.transcriptionAudioContext) {
      try {
        this.transcriptionAudioContext.close();
      } catch (e) {}
      this.transcriptionAudioContext = null;
    }

    if (this.audioStream()) {
      this.audioStream()!
        .getTracks()
        .forEach((track) => track.stop());
      this.audioStream.set(null);
    }

    if (this.socketSub) {
      this.socketSub.unsubscribe();
      this.socketSub = null;
    }

    const liveTranscriptionData: LiveTranscriptionPayload = {
      data: null,
      contentID: this.contentId,
      language: this.translate.currentLang,
      settings: { model: TranscriptionModels.CHIRP3 },
    };
    this.socketService.emitEvent('stream-end', liveTranscriptionData);
  }

  ngOnDestroy() {
    this.stopDictation();
  }
}
